import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { IoNotificationsOutline } from "react-icons/io5";
import config from "../../config";
import Toast from "../Common/Toast";

const PAGE_SIZE = 20;

const toBoolean = (value, fallback = false) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.toLowerCase() === "true";
  return fallback;
};

const safeParseUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null") || {};
  } catch {
    return {};
  }
};

const readStorage = (key) => {
  const fromLocal = localStorage.getItem(key);
  if (fromLocal) return fromLocal;
  const fromSession = sessionStorage.getItem(key);
  if (fromSession) return fromSession;
  return null;
};

const readCookie = (key) => {
  if (typeof document === "undefined") return null;
  const name = `${key}=`;
  const decodedCookie = decodeURIComponent(document.cookie || "");
  const parts = decodedCookie.split(";");

  for (let i = 0; i < parts.length; i += 1) {
    const c = parts[i].trim();
    if (c.startsWith(name)) {
      return c.substring(name.length);
    }
  }

  return null;
};

const getByPath = (obj, path) => {
  return path.split(".").reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : null), obj);
};

const pickFirst = (obj, paths = []) => {
  for (const path of paths) {
    const value = getByPath(obj, path);
    if (value) return value;
  }
  return null;
};

const getToken = (user) => {
  return (
    pickFirst(user, [
      "accessToken",
      "access_token",
      "accesstoken",
      "accessKey",
      "access_key",
      "token",
      "userToken",
      "user_token",
      "jwt",
      "jwtToken",
      "jwt_token",
      "idToken",
      "id_token",
      "authToken",
      "auth_token",
      "authorizationToken",
      "authorization_token",
      "data.accessToken",
      "data.access_token",
      "data.accessKey",
      "data.access_key",
      "data.token",
      "data.userToken",
      "data.jwt",
      "data.jwtToken",
      "data.authToken",
      "data.authorizationToken",
      "response.accessToken",
      "response.access_token",
      "response.accessKey",
      "response.access_key",
      "response.token",
      "response.userToken",
      "response.jwt",
      "response.jwtToken",
      "response.authToken",
      "response.authorizationToken",
    ]) ||
    readStorage("accessToken") ||
    readStorage("access_token") ||
    readStorage("accessKey") ||
    readStorage("access_key") ||
    readStorage("token") ||
    readStorage("userToken") ||
    readStorage("jwt") ||
    readStorage("jwtToken") ||
    readStorage("authToken") ||
    readStorage("authorizationToken") ||
    readStorage("Authorization") ||
    readCookie("accessToken") ||
    readCookie("accessKey") ||
    null
  );
};

const getWdAuthorizationToken = (user) => {
  return (
    pickFirst(user, [
      "wdAuthorizationToken",
      "wd_authorization_token",
      "wdauthorizationtoken",
      "wdAuthToken",
      "wd_auth_token",
      "authorizationToken",
      "authorization_token",
      "data.wdAuthorizationToken",
      "data.wd_authorization_token",
      "data.wdAuthToken",
      "data.wd_auth_token",
      "data.authorizationToken",
      "data.authorization_token",
      "response.wdAuthorizationToken",
      "response.wd_authorization_token",
      "response.wdAuthToken",
      "response.wd_auth_token",
      "response.authorizationToken",
      "response.authorization_token",
    ]) ||
    readStorage("wdAuthorizationToken") ||
    readStorage("wd_authorization_token") ||
    readStorage("wdAuthToken") ||
    readStorage("wd_auth_token") ||
    readStorage("authorizationToken") ||
    readStorage("authorization_token") ||
    null
  );
};

const getAuthorizationHeader = (user) => {
  const explicit =
    pickFirst(user, [
      "Authorization",
      "authorization",
      "data.Authorization",
      "data.authorization",
      "response.Authorization",
      "response.authorization",
    ]) || readStorage("Authorization");
  if (explicit) return explicit;

  const token = getToken(user);
  if (!token) return null;
  if (String(token).toLowerCase().startsWith("bearer ")) return token;

  return `Bearer ${token}`;
};

const extractBearerToken = (authorizationValue) => {
  if (!authorizationValue) return null;
  const value = String(authorizationValue).trim();
  if (!value) return null;
  if (value.toLowerCase().startsWith("bearer ")) {
    return value.substring(7).trim();
  }
  return value;
};

const buildAxiosConfig = () => {
  const user = safeParseUser();
  const headers = {};
  const authorization = getAuthorizationHeader(user);
  const wdAuthorizationToken = getWdAuthorizationToken(user);
  const directAccessToken =
    pickFirst(user, ["accessToken", "data.accessToken", "response.accessToken"]) ||
    readStorage("accessToken") ||
    readStorage("accessKey") ||
    readCookie("accessToken") ||
    readCookie("accessKey");
  const accessToken =
    directAccessToken ||
    extractBearerToken(authorization) ||
    wdAuthorizationToken ||
    null;
  const companyId =
    pickFirst(user, ["companyId", "company_id", "data.companyId", "response.companyId"]) ||
    readStorage("companyId");
  const email =
    pickFirst(user, ["email", "data.email", "response.email"]) ||
    readStorage("email");
  const roleId =
    pickFirst(user, ["roleId", "role_id", "role", "data.roleId", "response.roleId"]) ||
    readStorage("roleId") ||
    readStorage("role");
  const clientId =
    pickFirst(user, ["clientId", "client_id", "id", "data.clientId", "response.clientId"]) ||
    readStorage("clientId");
  const appUserId =
    pickFirst(user, ["appUserId", "app_user_id", "userId", "user_id", "id", "data.appUserId", "response.appUserId"]) ||
    readStorage("appUserId") ||
    readStorage("userId");

  if (authorization) {
    headers.Authorization = authorization;
  }

  if (wdAuthorizationToken) {
    headers.wdAuthorizationToken = wdAuthorizationToken;
  }
  if (accessToken) {
    headers.accessToken = String(accessToken);
    headers.accessKey = String(accessToken);
  }

  if (companyId !== null && companyId !== undefined && companyId !== "") {
    headers.companyId = String(companyId);
  }
  if (email) {
    headers.email = String(email);
  }
  if (roleId !== null && roleId !== undefined && roleId !== "") {
    headers.roleId = String(roleId);
  }
  if (clientId !== null && clientId !== undefined && clientId !== "") {
    headers.clientId = String(clientId);
  }
  if (appUserId !== null && appUserId !== undefined && appUserId !== "") {
    headers.appUserId = String(appUserId);
  }

  return {
    withCredentials: true,
    headers,
  };
};

const normalizeNotification = (item) => {
  const id = item?.id ?? item?.notificationId ?? item?.notification_id ?? null;
  const createdAt = item?.createdAt || item?.created_at || item?.timestamp || "";

  return {
    id,
    eventId: item?.eventId || item?.event_id || `${id || "n"}-${createdAt || Date.now()}`,
    title: item?.title || item?.eventType || item?.type || "Notification",
    body: item?.body || item?.message || item?.description || "",
    eventType: item?.eventType || item?.type || "",
    isRead: toBoolean(item?.isRead ?? item?.read, false),
    createdAt,
    raw: item,
  };
};

const extractUnreadCount = (data) => {
  if (typeof data === "number") return data;
  if (typeof data?.unreadCount === "number") return data.unreadCount;
  if (typeof data?.count === "number") return data.count;
  if (Array.isArray(data?.content)) {
    return data.content.filter((n) => !normalizeNotification(n).isRead).length;
  }
  return 0;
};

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
};

const NotificationBell = ({ isAuthenticated = false, compact = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "" });

  const dropdownRef = useRef(null);
  const sourceRef = useRef(null);
  const knownEventIdsRef = useRef(new Set());
  const hasInitializedRef = useRef(false);
  const audioContextRef = useRef(null);
  const lastSoundAtRef = useRef(0);

  const ensureAudioReady = useCallback(async () => {
    if (typeof window === "undefined") return null;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;

    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContextClass();
    }

    if (audioContextRef.current.state === "suspended") {
      try {
        await audioContextRef.current.resume();
      } catch {
        return audioContextRef.current;
      }
    }

    return audioContextRef.current;
  }, []);

  const playNotificationSound = useCallback(async () => {
    const now = Date.now();
    if (now - lastSoundAtRef.current < 900) return;

    const audioContext = await ensureAudioReady();
    if (!audioContext) return;

    lastSoundAtRef.current = now;

    try {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime);

      gainNode.gain.setValueAtTime(0.0001, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.08, audioContext.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + 0.24);

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.25);
    } catch {
      // Ignore audio playback issues so notifications continue working silently.
    }
  }, [ensureAudioReady]);

  const latest = useMemo(() => items.slice(0, PAGE_SIZE), [items]);

  const mergeIncoming = useCallback((incoming, prepend = false) => {
    const next = normalizeNotification(incoming);
    if (next.eventId) {
      knownEventIdsRef.current.add(next.eventId);
    }

    setItems((prev) => {
      const exists = prev.some((item) => item.eventId === next.eventId || (next.id && item.id === next.id));
      if (exists) return prev;
      if (prepend) return [next, ...prev];
      return [...prev, next];
    });

    if (!next.isRead) {
      setUnreadCount((count) => count + 1);
    }
  }, []);

  const fetchUnreadPayload = useCallback(async () => {
    const requestConfig = buildAxiosConfig();

    try {
      const unreadRes = await axios.get(
        `${config.BL_API_URL}/notifications/v1/unread?page=0&size=${PAGE_SIZE}`,
        requestConfig
      );
      return unreadRes?.data;
    } catch {
      // Fallback: some backend deployments currently return unread payload on unreadCount.
      const unreadCountRes = await axios.get(
        `${config.BL_API_URL}/notifications/v1/unreadCount`,
        requestConfig
      );
      return unreadCountRes?.data;
    }
  }, []);

  const loadUnread = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    try {
      const payload = await fetchUnreadPayload();
      const content = Array.isArray(payload?.content) ? payload.content : [];
      const normalizedItems = content.map(normalizeNotification);
      const newUnreadItems = normalizedItems.filter(
        (item) => !item.isRead && item.eventId && !knownEventIdsRef.current.has(item.eventId)
      );

      setItems(normalizedItems);
      knownEventIdsRef.current = new Set(normalizedItems.map((item) => item.eventId).filter(Boolean));

      if (hasInitializedRef.current && newUnreadItems.length > 0) {
        const latestNew = newUnreadItems[0];
        setToast({
          show: true,
          message:
            newUnreadItems.length > 1
              ? `${newUnreadItems.length} new notifications`
              : (latestNew.body || latestNew.title || "New notification"),
        });
        playNotificationSound();
      }

      hasInitializedRef.current = true;
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, fetchUnreadPayload, playNotificationSound]);

  const loadUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const res = await axios.get(
        `${config.BL_API_URL}/notifications/v1/unreadCount`,
        buildAxiosConfig()
      );
      setUnreadCount(extractUnreadCount(res?.data));
    } catch {
      setUnreadCount(0);
    }
  }, [isAuthenticated]);

  const markRead = useCallback(async (id) => {
    if (!id) return;

    try {
      await axios.post(
        `${config.BL_API_URL}/notifications/v1/${id}/markRead`,
        {},
        buildAxiosConfig()
      );

      setItems((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
      );
      setUnreadCount((count) => (count > 0 ? count - 1 : 0));
    } catch {
      // Intentionally ignore mark-read API failures to keep UI stable.
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await axios.post(
        `${config.BL_API_URL}/notifications/v1/markAllRead`,
        {},
        buildAxiosConfig()
      );

      setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
      // Intentionally ignore mark-all failures to keep UI stable.
    }
  }, []);

  const refreshNotifications = useCallback(async () => {
    await Promise.all([loadUnread(), loadUnreadCount()]);
  }, [loadUnread, loadUnreadCount]);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsConnected(false);
      setUnreadCount(0);
      setItems([]);
      knownEventIdsRef.current = new Set();
      hasInitializedRef.current = false;
      if (sourceRef.current) {
        if (typeof sourceRef.current.close === "function") {
          sourceRef.current.close();
        } else if (typeof sourceRef.current.abort === "function") {
          sourceRef.current.abort();
        }
        sourceRef.current = null;
      }
      return;
    }

    loadUnread();
    loadUnreadCount();

    const user = safeParseUser();
    const authHeader = getAuthorizationHeader(user);
    const wdAuthorizationToken = getWdAuthorizationToken(user);
    const directAccessToken =
      pickFirst(user, ["accessToken", "data.accessToken", "response.accessToken"]) ||
      readStorage("accessToken") ||
      readStorage("accessKey") ||
      readCookie("accessToken") ||
      readCookie("accessKey");
    const accessToken =
      directAccessToken ||
      extractBearerToken(authHeader) ||
      wdAuthorizationToken ||
      null;

    const streamQuery = new URLSearchParams();
    if (accessToken) {
      streamQuery.set("token", String(accessToken));
      // Keep compatibility with older stream auth parser if present.
      streamQuery.set("accessToken", String(accessToken));
    }

    const streamUrl =
      streamQuery.toString().length > 0
        ? `${config.BL_API_URL}/notifications/v1/stream?${streamQuery.toString()}`
        : `${config.BL_API_URL}/notifications/v1/stream`;
    const es = new EventSource(streamUrl);
    sourceRef.current = es;
    es.onopen = () => setIsConnected(true);
    es.onerror = () => setIsConnected(false);

    const tryParse = (value) => {
      if (value === null || value === undefined) return null;
      if (typeof value === "object") return value;
      if (typeof value !== "string") return null;
      const trimmed = value.trim();
      if (!trimmed) return null;
      try {
        return JSON.parse(trimmed);
      } catch {
        return null;
      }
    };

    const handleIncomingEvent = (event) => {
      if (!event?.data) return;

      try {
        const incoming = tryParse(event.data);
        if (!incoming) return;

        const candidates = [];
        if (Array.isArray(incoming?.content)) {
          candidates.push(...incoming.content);
        } else if (Array.isArray(incoming?.notifications)) {
          candidates.push(...incoming.notifications);
        } else if (incoming?.notification) {
          candidates.push(incoming.notification);
        } else if (incoming?.data) {
          if (Array.isArray(incoming.data)) {
            candidates.push(...incoming.data);
          } else {
            candidates.push(tryParse(incoming.data) || incoming.data);
          }
        } else {
          candidates.push(incoming);
        }

        if (candidates.length === 0) {
          return;
        }

        candidates.forEach((entry) => {
          if (entry) mergeIncoming(entry, true);
        });

        const normalized = normalizeNotification(candidates[0]);
        setToast({
          show: true,
          message: normalized.body || normalized.title || "New notification",
        });
        playNotificationSound();

        // Ensure badge always reflects server truth even if payload shape varies.
        loadUnreadCount();
      } catch {
        // Ignore malformed SSE payloads.
      }
    };

    es.addEventListener("notification", handleIncomingEvent);
    es.onmessage = handleIncomingEvent;

    return () => {
      es.close();
      sourceRef.current = null;
    };
  }, [isAuthenticated, loadUnread, loadUnreadCount, mergeIncoming, playNotificationSound]);

  useEffect(() => {
    return () => {
      if (audioContextRef.current && typeof audioContextRef.current.close === "function") {
        audioContextRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    const onDocClick = (event) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, []);

  if (!isAuthenticated) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => {
          ensureAudioReady();
          const nextOpen = !isOpen;
          setIsOpen(nextOpen);
          if (nextOpen) {
            refreshNotifications();
          }
        }}
        className="relative text-gray-700 hover:text-blue-600 transition-colors"
        title="Notifications"
      >
        <IoNotificationsOutline className={compact ? "text-xl" : "text-2xl"} />
        {unreadCount > 0 && (
          <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] leading-[18px] text-center font-semibold">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-3 w-[340px] max-w-[92vw] bg-white border border-gray-200 rounded-lg shadow-xl z-[70]">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-gray-800">Notifications</div>
              <div className="text-[11px] text-gray-500 mt-0.5">
                <span className={`inline-block w-2 h-2 rounded-full mr-1 ${isConnected ? "bg-green-500" : "bg-gray-400"}`}></span>
                {isConnected ? "Live" : "Offline"}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="text-xs text-blue-600 hover:underline"
                onClick={refreshNotifications}
              >
                Refresh
              </button>
              <button
                type="button"
                className="text-xs text-blue-600 hover:underline"
                onClick={markAllRead}
              >
                Mark all read
              </button>
            </div>
          </div>

          <div className="max-h-[360px] overflow-y-auto">
            {loading ? (
              <div className="p-4 text-sm text-gray-500">Loading notifications...</div>
            ) : latest.length === 0 ? (
              <div className="p-4 text-sm text-gray-500">No unread notifications.</div>
            ) : (
              latest.map((notification) => (
                <div
                  key={notification.eventId}
                  className={`px-4 py-3 border-b border-gray-100 hover:bg-gray-50 ${notification.isRead ? "opacity-70" : ""}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-800 truncate">
                        {notification.title}
                      </div>
                      <div className="text-xs text-gray-600 mt-1 whitespace-normal break-words">
                        {notification.body || "No details"}
                      </div>
                      <div className="text-[11px] text-gray-400 mt-1">
                        {formatDateTime(notification.createdAt)}
                      </div>
                    </div>
                    {!notification.isRead && (
                      <button
                        type="button"
                        className="text-[11px] text-blue-600 hover:underline shrink-0"
                        onClick={() => markRead(notification.id)}
                      >
                        Mark read
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <Toast
        show={toast.show}
        message={toast.message}
        type="info"
        onClose={() => setToast({ show: false, message: "" })}
      />
    </div>
  );
};

export default NotificationBell;
